import { LinkExternal01 } from '@ethsign/icons';
import { diffPDF } from '@ethsign/pdf-editor';
import { Button, ScrollArea } from '@ethsign/ui';
import { ethSignAesDecrypt, hashSha256 } from '@ethsign/utils-web';
import { verifyMessage } from 'ethers';
import { sortBy } from 'lodash-es';

import { useEffect, useRef, useState } from 'react';

import {
  AddIcon,
  ArrowRightIcon,
  CheckIcon,
  DecryptContractModal,
  InfoItem,
  LogoIcon,
  NetworkTag,
  ShieldIcon,
  SubtractIcon,
  WarningIcon
} from '@/components';
import { CopyText } from '@/components/CopyText';
import { LottieAnimation } from '@/components/LottieAnimation';
import { ENVS } from '@/constants';
import { useRouter } from '@/hooks';
import { getContractByID, getContractDataByTx } from '@/services';
import {
  addAnnotationToPDF,
  convertXmlToJSON,
  dateFormat,
  getDecryptedFile,
  getPDFSubject,
  getReadOnlyTextXml,
  parseFileSize,
  reportGAEvent,
  WalletType
} from '@/utils';

import { fileToBuffer } from '@/utils/file';

interface Props {
  onValidateOther: () => void;
  file: File;
}

interface ISubject {
  id: string;
  enableEncryption: boolean;
  version?: number;
}

const SignerItem = ({ signer, i }: { signer: any; i: number }) => {
  const [isFold, setIsFold] = useState(true);
  const isEvmOrBtc = [WalletType.EVM, WalletType.BITCOIN].includes(signer?.network);

  return (
    <div className="px-8 py-6 border rounded-xl border-[#EAECF0] mt-5">
      <div className="flex flex-row justify-between items-start">
        <div>
          <InfoItem
            title="Signer Address:"
            text={
              <div className={'flex gap-[8px] items-center'}>
                <CopyText displayText={signer.address} text={signer.address}></CopyText>
                <NetworkTag network={signer.network} />
              </div>
            }
          ></InfoItem>
        </div>
        <div className="flex flex-row items-center px-2 py-1 rounded-full bg-slime-50 text-slime-500 text-[10px]/[12px]">
          <CheckIcon></CheckIcon>
          <span className="ml-1">Verified by EthSign</span>
        </div>
      </div>
      <div className="flex flex-row mt-4">
        {/*<div>*/}
        {/*  <InfoItem title="Signature CID:" text={'-'}></InfoItem>*/}
        {/*</div>*/}
        <div className="ml-0">
          <InfoItem title="Date:" text={signer?.date}></InfoItem>
        </div>
      </div>
      {!isFold && (
        <>
          <div className="flex flex-row  mt-4">
            <InfoItem title="Signing Algorithm:" text={isEvmOrBtc ? 'ECDSA' : 'ED25519'}></InfoItem>
            <div className="ml-10">
              <InfoItem title="Signing Index (Signing Order):" text={i}></InfoItem>
            </div>
          </div>
          {/*<div className="flex flex-row mt-4">*/}
          {/*  <InfoItem title="Signature Application Entity:" text={'-'}></InfoItem>*/}
          {/*  <div className="ml-10">*/}
          {/*    <InfoItem title="Signature Hash Algorithm:" text={'-'}></InfoItem>*/}
          {/*  </div>*/}
          {/*  <div className="ml-10">*/}
          {/*    <InfoItem title="Signature Time Source:" text={'-'}></InfoItem>*/}
          {/*  </div>*/}
          {/*</div>*/}
        </>
      )}
      <div
        className="mt-4 text-primary-900 text-xs font-medium flex flex-row"
        onClick={() => {
          setIsFold(!isFold);
        }}
      >
        {isFold ? <AddIcon fill="#CF5C10"></AddIcon> : <SubtractIcon></SubtractIcon>}
        <span className="ml-1 cursor-pointer">More details</span>
      </div>
    </div>
  );
};

export default function VerifyDetail(props: Props) {
  const { onValidateOther, file } = props;
  const [subject, setSubject] = useState<ISubject | null>(null);
  const [openDecryptModal, setOpenDecryptModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const router = useRouter();
  const contractRef = useRef<{ contract: any; tx: string }>();

  const compareFile = async ({ password, subject }: { password?: string; subject: ISubject }) => {
    // throw Error('not implemented');
    const { contract, tx } = contractRef.current!;
    const decryptedFile = await getDecryptedFile(contract.contractContent, password);
    const fileStr = decryptedFile.fileString;
    const annotationXml = await getReadOnlyTextXml(decryptedFile.annotation);
    const signedData = contract.signers.map((x: any) => JSON.parse(x.signingContent)?.signingContent);
    const annotations = await Promise.all(
      signedData.map(async (x: string) => {
        const annotation = password ? await ethSignAesDecrypt(x, password) : x;
        return convertXmlToJSON(annotation);
      })
    );
    // 区别新版旧版
    if (subject.version === 1) {
      annotations.push(annotationXml);
    }
    const composeFileBuffer = await addAnnotationToPDF(fileStr, annotations, subject);
    const fileBuffer = await fileToBuffer(file);
    const diff = await diffPDF(composeFileBuffer, fileBuffer);

    const data = {
      valid: diff.consistent,
      signers: sortBy(contract.signers, (it) => Number(it.signingAt)).map((it) => {
        const [network, address] = it.signer.split('|');
        return {
          ...it,
          address,
          network,
          date: dateFormat(Number(it.signingAt), 'datetime')
        };
      }),
      tx: tx,
      contract
    };
    setData(data);
  };

  const verifyPassword = async (password?: string) => {
    const { contract } = contractRef.current!;
    try {
      const file = await getDecryptedFile(contract.contractContent, password);
      return file;
    } catch (e) {
      console.log(e);
      throw Error('password not incorrect');
    }
  };

  const getData = async ({ subject }: { subject: ISubject }) => {
    setLoading(true);
    try {
      const contractRes = await getContractByID(subject.id);
      const tx = contractRes.txId;
      if (!tx) {
        throw Error('tx not found');
      }
      const res = await getContractDataByTx(tx);
      const { contract: contractStr, contractHash, signature } = res.data;
      const computeHash = await hashSha256(contractStr);
      console.log(computeHash, contractHash, computeHash === contractHash);
      if (computeHash !== contractHash) {
        // todo
      }
      const contract = JSON.parse(contractStr);
      contractRef.current = {
        contract,
        tx
      };
      const signAddress = await verifyMessage(contractHash, signature);
      const valid = signAddress.toLowerCase() === ENVS.VITE_ETHSIGN_SIGN_ADDRESS!.toLowerCase();
      if (!valid) {
        // todo
      }
      if (subject?.enableEncryption) {
        setOpenDecryptModal(true);
      } else {
        await compareFile({ subject });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getSubject = async () => {
    try {
      setLoading(true);
      const subject = await getPDFSubject(file);
      if (!subject) {
        setLoading(false);
        return;
      }
      setSubject(subject);
      reportGAEvent('pc_contract_verifyContract', subject);
      await getData({
        subject
      });
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubject();
  }, [file]);

  const handleConfirm = async (v: string): Promise<boolean> => {
    try {
      await verifyPassword(v);
      setLoading(true);
      setOpenDecryptModal(false);
      await compareFile({ password: v, subject: subject! });
      setLoading(false);
      return true;
    } catch (e) {
      console.log(e);
      setLoading(false);
      return false;
    }
  };

  return (
    <div className="flex flex-row bg-white">
      <DecryptContractModal
        hiddenCloseIcon
        footer={
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              onValidateOther();
            }}
          >
            Back to Validate
          </Button>
        }
        open={openDecryptModal}
        onOpenChange={(v) => {
          setOpenDecryptModal(v);
        }}
        onConfirm={handleConfirm}
      />
      <div className="w-[480px] pl-16 pt-[64px] min-h-screen bg-gray-50 box-border">
        <div
          className="cursor-pointer"
          onClick={() => {
            router.push('/dashboard');
          }}
        >
          <LogoIcon></LogoIcon>
        </div>
        <div className="mt-[69px] text-[32px]/[40px] text-gray-600 font-bold">Validation Result</div>
        <div className="mt-8 flex flex-row">
          <object
            data="https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/pdf2_240723072708.webp"
            width={52}
            height={64}
          ></object>
          <div className="flex flex-col justify-center items-start ml-4 flex-1 pr-4">
            <span className="font-semiBold text-xl font-semibold text-gray-600">{file?.name}</span>
            <span className="mt-1 text-sm text-gray-900 ">{parseFileSize(file?.size)}</span>
          </div>
        </div>
        {data ? (
          <div className="mt-14 ">
            <div className="mb-6">
              <InfoItem
                title="Created Date:"
                interval={2}
                text={dateFormat(data?.contract?.createAt, 'datetime')}
              ></InfoItem>
            </div>
            <div className="mb-6">
              <InfoItem
                title="Document Key:"
                interval={2}
                text={<CopyText displayText={subject?.id || ''} text={subject?.id || ''}></CopyText>}
              ></InfoItem>
            </div>
            <div className="mb-6">
              <InfoItem
                title="Document Storage CID:"
                interval={2}
                text={
                  <a
                    className="flex flex-row items-center"
                    target="_blank"
                    href={`https://viewblock.io/arweave/tx/${data?.tx}`}
                    rel="noreferrer"
                  >
                    <span className="inline-block max-w-[320px] truncate" title={data?.tx}>
                      {data?.tx}
                    </span>
                    <LinkExternal01
                      size={20}
                      color={'#667085'}
                      className={'cursor-pointer ml-2 inline-block'}
                    ></LinkExternal01>
                  </a>
                }
              ></InfoItem>
            </div>
            <div className="mb-6">
              <InfoItem title="Storage Provider:" interval={2} text={'Arweave'}></InfoItem>
            </div>
          </div>
        ) : null}
      </div>
      <ScrollArea className="flex flex-col items-center flex-1 pl-20 pr-20 pt-[100px] h-[100vh]">
        {loading ? (
          <div className="relative flex flex-col justify-center items-center">
            <LottieAnimation
              className="ml-16 mt-10 w-[560px] h-[336px]"
              url="https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/verify_250224090848.json"
            ></LottieAnimation>
            <div className=" text-md text-gray-900  w-full text-center">
              ⏳ Indexing on-chain data and running cryptographic functions
            </div>
          </div>
        ) : (
          <>
            {data && data.valid ? (
              <div className="mb-10 w-full">
                <div className="font-semiBold font-semibold text-[25px]/[34px]">Signatures in this document</div>
                {data?.signers?.map((signer: any, i: number) => {
                  return <SignerItem signer={signer} i={i} key={i} />;
                })}
              </div>
            ) : (
              <div className="mb-6 w-full rounded-xl flex flex-row h-[104px] pl-10 border items-center border-gray-200">
                <span className="w-6">
                  <WarningIcon></WarningIcon>
                </span>
                <span className="ml-2.5 text-md font-medium text-gray-900">
                  We couldn&apos;t verify the validity of this file.
                </span>
              </div>
            )}
            <div className="bg-gray-50 rounded-xl flex flex-col justify-center pt-5 pb-5 w-full">
              <div className="flex flex-row ml-8 mr-8">
                <span className="w-6">
                  <ShieldIcon fill={data ? '#12B76A' : '#98A2B3'} />
                </span>
                <span className="ml-2 text-md font-semiBold font-semibold text-gray-900">
                  Validated by EthSign on {dateFormat(new Date(), 'YYYY-MM-DD HH:mm:ss')}
                </span>
              </div>
              <div className="ml-16 mt-2 text-sm text-gray-600">
                This validation result is generated from on-chain records and cryptographic functions.<br></br>For more
                details,{' '}
                <a
                  target="_blank"
                  href="https://docs.ethsign.xyz/ethsign/"
                  className="font-medium text-primary-900"
                  rel="noreferrer"
                >
                  see EthSign documentation.
                </a>
              </div>
            </div>
            <div className="pt-[126px] pb-5 flex items-center justify-end w-full">
              {/*<Button*/}
              {/*  variant={'outline'}*/}
              {/*  onClick={() => {*/}
              {/*    onValidateOther();*/}
              {/*  }}*/}
              {/*>*/}
              {/*  Share*/}
              {/*  <span className="ml-2">*/}
              {/*    <Share07 width={16} height={16} />*/}
              {/*  </span>*/}
              {/*</Button>*/}
              <Button
                onClick={() => {
                  onValidateOther();
                }}
              >
                Validate another file
                <span className="ml-2">
                  <ArrowRightIcon width={16} height={16} />
                </span>
              </Button>
            </div>
          </>
        )}
      </ScrollArea>
    </div>
  );
}
